// Account template component for the account page.

// Imports ----------------------------------------------------------------------------------------------------------

import Body from '@components/sections/account/accountBody';
import Container from '../atoms/container/accountMainContainer';
import Focus from '../../../shared/types/enums/focus';
import QR from '@components/sections/account/accountQR';
import Viewport from '../atoms/container/mainContainer';
import useBackDoorCombination from '../../utils/hooks/useBackDoorCombination';
import useBackKeyboard from '../../../shared/components/utils/hooks/useBackKeyboard';
import useMenu from '../../../shared/components/utils/hooks/useMenu';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import {
  focusSelector,
  setFocus,
} from '../../../shared/store/slices/focus.slice';

/**
 * Account template component for the account page.
 * Handles the account information display and CMP integration.
 *
 * @returns {JSX.Element} Account container with account information, CMP button and QR code
 */
export default function AccountTemplate() {
  // Hooks
  const { props } = useAccountTemplate();

  // Render
  return (
    <Viewport>
      <Container {...props}>
        <Body />
        <QR />
      </Container>
    </Viewport>
  );
}

/**
 * Hook to use the account template.
 * Handles the account template logic.
 *
 * @returns {Object} Object with the props for the account container
 */
function useAccountTemplate() {
  // Dispatch

  const dispatch = useAppDispatch();

  // Selectors

  const { focus } = useAppSelector(focusSelector);

  // Callbacks

  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.ACCOUNT));
  }, []);

  // Effect hooks

  useEffect(() => {
    dispatch(setFocus(Focus.ACCOUNT));
  }, []);

  // Menu hook

  useMenu(null, true);

  // Keyboard hooks

  useBackDoorCombination();

  useBackKeyboard();

  // Memoized values

  const props = useMemo(() => {
    return {
      animation: focus === Focus.MENU ? 'main-content-out' : 'main-content-in',
      force: true,
      id: 'accountContainer',
      onMouseEnter,
    };
  }, [focus]);

  // Return values

  return { props };
}
